import { DatePicker, Input, Popover, Radio, RadioChangeEvent } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { DateFormat } from '@shared/constants';

const OnlyDatePicker = styled.div.attrs({ className: 'only-date-picker' })`
  & > * {
    position: unset !important;
  }

  .ant-picker {
    height: 0;
    width: 0;
    overflow: hidden;
    opacity: 0;
  }

  .ant-picker-dropdown {
    position: unset !important;
    margin-bottom: -35px;
  }

  .ant-picker-panel-container {
    box-shadow: none;
  }
`;

export type AdvancedDateRangePickerProps = {
  value: [Dayjs, Dayjs] | null;
  onChange: (value: [Dayjs, Dayjs] | null) => void;
};

export const AdvancedDateRangePicker: FC<AdvancedDateRangePickerProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState<boolean>(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const last7Days = useMemo(() => `${dayjs().subtract(7, 'day').format(DateFormat.DATE)},${dayjs().format(DateFormat.DATE)}`, []);
  const last31Days = useMemo(() => `${dayjs().subtract(31, 'day').format(DateFormat.DATE)},${dayjs().format(DateFormat.DATE)}`, []);
  const last90Days = useMemo(() => `${dayjs().subtract(90, 'day').format(DateFormat.DATE)},${dayjs().format(DateFormat.DATE)}`, []);
  const last12Months = useMemo(() => `${dayjs().subtract(12, 'month').format(DateFormat.DATE)},${dayjs().format(DateFormat.DATE)}`, []);

  const stringToRange = useCallback((_values: string): [Dayjs, Dayjs] => {
    const values = _values.split(',');

    if (values.length !== 2) {
      throw new Error();
    }

    return values.map((v) => dayjs(v, DateFormat.DATE)) as [Dayjs, Dayjs];
  }, []);

  const [picker, setPicker] = useState<string>('range');
  const [range, setRange] = useState(last31Days);

  const displayValue = useMemo(() => {
    if (value) {
      return value.map((date) => date.format(DateFormat.DATE)).join(' - ');
    }

    if (range === 'all') {
      return 'Başdan sona';
    }

    return undefined;
  }, [range, value]);

  const onRangeChange = useCallback((event: RadioChangeEvent) => setRange(event.target.value), []);

  const onDateChange = useCallback((v: [Dayjs | null, Dayjs | null] | null) => {
    if (v) {
      const [fromDate, toDate] = v;

      if (fromDate && toDate) {
        onChangeRef.current([fromDate, toDate]);
      }
    }
  }, []);

  const onChangePicker = useCallback((event: RadioChangeEvent) => {
    setPicker(event.target.value);
  }, []);

  const onOnlyPickerChange = useCallback(
    (v: Dayjs | null, p: string) => {
      if (v) {
        onDateChange([v.startOf(p as any), v.startOf(p as any).add(1, p as any)]);
      }
    },
    [onDateChange],
  );

  const getPickerPopupContainer = useCallback(() => {
    const target = document.querySelector('.only-date-picker') || document.body;

    return target as HTMLElement;
  }, []);

  useEffect(() => {
    if (range !== 'custom' && range !== 'all') {
      onChangeRef.current(stringToRange(range));
    } else {
      onChangeRef.current(null);
    }
  }, [range, stringToRange]);

  useEffect(() => {
    if (value) {
      setOpen(false);
    }
  }, [value]);

  const styles = useMemo(
    () => ({
      wrapper: { width: 280 },
      pickerRadioGroup: { display: 'flex' },
      pickerRadioItem: { flex: 1, textAlign: 'center' as const },
      radioWrapper: { padding: '0 0 8px 0' },
      radio: { display: 'block' },
      selectWrapper: { marginTop: 8 },
      rangePicker: { width: '100%' },
      input: { width: 205 },
    }),
    [],
  );

  const renderOverlay = (
    <div style={styles.wrapper}>
      <div>
        <Radio.Group style={styles.pickerRadioGroup} onChange={onChangePicker} value={picker}>
          <Radio.Button style={styles.pickerRadioItem} value="range">
            Aralıq
          </Radio.Button>
          <Radio.Button style={styles.pickerRadioItem} value="week">
            Həftə
          </Radio.Button>
          <Radio.Button style={styles.pickerRadioItem} value="month">
            Ay
          </Radio.Button>
          <Radio.Button style={styles.pickerRadioItem} value="year">
            İl
          </Radio.Button>
        </Radio.Group>
      </div>
      <OnlyDatePicker>
        {picker !== 'range' && <DatePicker value={value?.[0]} onChange={(v) => onOnlyPickerChange(v, picker)} open={true} picker={picker as any} getPopupContainer={getPickerPopupContainer} />}
      </OnlyDatePicker>
      {picker === 'range' && (
        <div>
          <div style={styles.radioWrapper}>
            <Radio.Group value={range} onChange={onRangeChange}>
              <Radio style={styles.radio} value={last7Days}>
                Son 7 gün
              </Radio>
              <Radio style={styles.radio} value={last31Days}>
                Son 31 gün
              </Radio>
              <Radio style={styles.radio} value={last90Days}>
                Son 90 gün
              </Radio>
              <Radio style={styles.radio} value={last12Months}>
                Son 12 ay
              </Radio>
              <Radio style={styles.radio} value="custom">
                Aralıq
              </Radio>
            </Radio.Group>
          </div>
          <div style={styles.selectWrapper}>
            <DatePicker.RangePicker onChange={(v) => onDateChange(v as [Dayjs | null, Dayjs | null] | null)} value={value} disabled={range !== 'custom'} style={styles.rangePicker} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} trigger="click" content={renderOverlay} placement="bottomRight">
      <Input style={styles.input} value={displayValue} placeholder="Tarix seçin..." readOnly />
    </Popover>
  );
};
